import { MDText } from 'i18n-react';

import cypressIds from 'Components/cypress_ids.json';
import { DownloadCSV } from 'Components/DownloadCSV/DownloadCSV';
import {
	triggerMarketplaceCSVDownloadButtonClickedEvent,
	triggerMarketplaceDownloadCSVAbortedEvent,
	triggerMarketplaceDownloadCSVFailedEvent,
	triggerMarketplaceDownloadCSVMenuOpenButtonClickedEvent,
	triggerMarketplaceDownloadCSVMenuOpenButtonHoveredEvent,
	triggerMarketplaceDownloadCSVSuccessEvent,
} from 'Services/analytics/events';
import { generateVehicleListCsv, getVehicleListCsv } from 'Services/api/vehicles';
import type { GetVehicleListRequestQuery } from 'Services/api/vehicles.types';
import { getVehicleListQuery } from 'Services/dataProvider.helpers';
import type { User } from 'Types/user';
import { useCalculateQueryFromFilters } from 'Utilities/hooks/query/useCalculateQueryFromFilters';
import useUser from 'Utilities/hooks/useUser';
import { API_ROUTES } from 'Utilities/routes';
import { parseQueryParams } from 'Utilities/utils';

import texts from './VehicleListDownloadCsv.texts';

import styles from './VehicleListDownloadCsv.module.scss';

const T = new MDText(texts);
const translate = (text: string) => String(T.translate(text));

type GenerateCsvProps = {
	dealerId: User['dealerId'];
	params: GetVehicleListRequestQuery;
};

const generateCsv = async ({ dealerId, params }: GenerateCsvProps) => {
	const vehicleListParams = { ...params, distanceFromDealerId: dealerId };
	const query = getVehicleListQuery(vehicleListParams);
	await generateVehicleListCsv(query);
};

// Temporary whilst we fix incident
interface VehicleListDownloadCsvProps {
	hideFilteredDownload?: boolean;
}

export const VehicleListDownloadCsv = ({ hideFilteredDownload = false }: VehicleListDownloadCsvProps): JSX.Element => {
	const { serverQuery } = useCalculateQueryFromFilters();
	const params = parseQueryParams(serverQuery);
	const { dealerId } = useUser();

	const pollingProps = {
		fetchCsv: getVehicleListCsv,
		filenameTitle: 'Motorway vehicles',
		generateCsv: () => generateCsv({ dealerId, params }),
		queryKey: ['getVehicleListCsvPolling'],
	};

	const handlers = {
		onDynamicDownloadButtonClick: triggerMarketplaceDownloadCSVMenuOpenButtonClickedEvent,
		onDynamicDownloadButtonHover: triggerMarketplaceDownloadCSVMenuOpenButtonHoveredEvent,
		onPollingAborted: triggerMarketplaceDownloadCSVAbortedEvent,
		onPollingError: triggerMarketplaceDownloadCSVFailedEvent,
		onPollingSuccess: triggerMarketplaceDownloadCSVSuccessEvent,
		onPopoverDownloadButtonClick: triggerMarketplaceCSVDownloadButtonClickedEvent,
	};

	return (
		<DownloadCSV
			isDynamic
			className={styles.container}
			cypressId={cypressIds.buttons.downloadCsvVehicleListPopoverOpen}
			dynamicDownloadOptions={{
				alignment: 'end',
				cypressId: cypressIds.buttons.downloadCsvVehicleListDownload,
				items: [
					{
						cypressId: cypressIds.inputs.vehicleListDownloadCsv.allVehicles,
						id: translate('options.allVehicles.id'),
						label: translate('options.allVehicles.label'),
						staticDownloadUrl: API_ROUTES.GET_MARKETPLACE_VEHICLE_LIST_CSV,
					},
					...(!hideFilteredDownload
						? [
								{
									cypressId: cypressIds.inputs.vehicleListDownloadCsv.filteredVehicles,
									id: translate('options.filteredVehicles.id'),
									label: translate('options.filteredVehicles.label'),
								},
							]
						: []),
				],
				title: translate('title'),
			}}
			handlers={handlers}
			pollingProps={pollingProps}
		/>
	);
};
